// /UI/Dropzone/utils/helpers.js
// Using relative import paths that match your project structure
// If these paths don't work, you'll need to adjust them based on your project
import { getExtensionVideoFile } from '../../../../helpers/getExtensionVideoFile';
import formatFileSize from '../../../../helpers/getformatFileSize';

/**
 * Creates a unique file ID
 * @param {File} file - The file object
 * @returns {string} Unique ID for the file
 */
export const createFileId = file => {
  return `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Processes a file to determine if it's a video and creates the file metadata
 * @param {File} file - The original file object
 * @returns {Promise<Object>} - Processed file metadata
 */
export const processFile = async file => {
  const isVideo = await getExtensionVideoFile(file.name);

  return {
    file,
    id: createFileId(file),
    name: file.name,
    size: file.size,
    type: file.type,
    isVideo,
    preview: URL.createObjectURL(file),
    dateAdded: new Date(),
  };
};

/**
 * Clean up file preview URLs to prevent memory leaks
 * @param {Array} files - Array of file objects with preview URLs
 */
export const cleanupFilePreviewUrls = files => {
  files.forEach(file => {
    if (file.preview) URL.revokeObjectURL(file.preview);
  });
};

/**
 * Generates error messages for rejected files
 * @param {Object} rejectedFile - File rejected by dropzone
 * @returns {string} - Formatted error message
 */
export const getFileRejectionMessage = rejectedFile => {
  const error = rejectedFile.errors[0];
  if (error.code === 'file-too-large') {
    return `"${rejectedFile.file.name}" is too large (${formatFileSize(rejectedFile.file.size)}). Max size is ${formatFileSize(error.maxSize)}.`;
  }
  return `"${rejectedFile.file.name}": ${error.message}`;
};

/**
 * Checks if upload can proceed based on files and status
 * @param {Array} files - Array of files
 * @param {number} videoCount - Number of video files
 * @param {boolean} isUploading - Whether upload is in progress
 * @param {boolean} isComplete - Whether upload is complete
 * @param {number} videoLimit - Maximum number of videos allowed
 * @returns {boolean} - Whether upload can proceed
 */
export const canUploadFiles = (files, videoCount, isUploading, isComplete, videoLimit) => {
  return files.length > 0 && videoCount <= videoLimit && !isUploading && !isComplete;
};

/**
 * Updates hidden form fields with media files data
 * @param {Object} mediaFiles - Object containing image and video arrays
 */
export const updateHiddenFormFields = mediaFiles => {
  const formImageArray = document.getElementById('imageArray');
  const formVideoArray = document.getElementById('videoArray');
  const formDropSubmit = document.getElementById('dropSubmit');

  // Update the hidden input values to include both existing and new values
  if (formImageArray && mediaFiles.images.length > 0) {
    const currentValues = formImageArray.value ? formImageArray.value.split(',') : [];
    formImageArray.value = [...new Set([...currentValues, ...mediaFiles.images])].join(',');
  }

  if (formVideoArray && mediaFiles.videos.length > 0) {
    const currentValues = formVideoArray.value ? formVideoArray.value.split(',') : [];
    formVideoArray.value = [...new Set([...currentValues, ...mediaFiles.videos])].join(',');
  }

  if (formDropSubmit) {
    formDropSubmit.value = 'true';
  }
};

/**
 * Get order ID from the form if available
 * @returns {string} - Order ID or empty string
 */
export const getOrderId = () => {
  return document.getElementById('externalOrderID')?.value || '';
};

// Re-export format file size for convenience
// Re-export format file size for convenience
export { formatFileSize, getExtensionVideoFile };
